/**
 * @file modules/brand_feedback.js
 * @description Client-side behavior for the brand FAQ feedback form.
 *              - Wires the NPS 1–5 pills (aria-checked + hidden `rating` input)
 *              - Validates required fields on submit
 *              - POSTs via fetch to the current path and swaps to a success
 *                message, matching the BrandsController#submit JSON contract
 *              - Surfaces server errors via the reusable fragno-toast
 */

import { showToast } from "./toast";
import { dispatchTrack } from "../analytics/shop-analytics-track.js";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEFAULT_ERROR_MESSAGE = "Something went wrong. Please try again.";

function getCsrfToken() {
  return (
    document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content") || ""
  );
}

function getFragnoInputValue(field) {
  const fragnoInput = field?.querySelector("fragno-input");
  if (!fragnoInput) return { fragnoInput: null, value: "" };
  const nativeInput =
    fragnoInput.querySelector("input.fragno-input") ||
    fragnoInput.querySelector("input");
  return { fragnoInput, value: nativeInput?.value?.trim() || "" };
}

function setFragnoError(fragnoInput, message) {
  if (!fragnoInput) return;
  fragnoInput.setAttribute("error", "");
  fragnoInput.setAttribute("error-message", message);
}

function clearFragnoError(fragnoInput) {
  if (!fragnoInput) return;
  fragnoInput.removeAttribute("error");
  fragnoInput.removeAttribute("error-message");
}

function setFieldError(field, message) {
  if (!field) return;
  field.classList.add("is-invalid");
  const errorEl = field.querySelector(".brand-feedback__error");
  if (errorEl) errorEl.textContent = message;
}

function clearFieldError(field) {
  if (!field) return;
  field.classList.remove("is-invalid");
  const errorEl = field.querySelector(".brand-feedback__error");
  if (errorEl) errorEl.textContent = "";
}

function initRatingPills(container) {
  const hidden = container.querySelector("[data-brand-feedback-rating-input]");
  const options = Array.from(
    container.querySelectorAll("[data-brand-feedback-rating-option]"),
  );
  if (options.length === 0) return;

  // `track` defaults to true; arrow-key navigation passes false so we don't
  // emit a rating event for every focus change while the user is browsing.
  const select = (option, { focus = false, track = true } = {}) => {
    const value = option.getAttribute("data-brand-feedback-rating-option");
    if (hidden) hidden.value = value;
    options.forEach((other) => {
      const isSelected = other === option;
      other.setAttribute("aria-checked", isSelected ? "true" : "false");
      other.setAttribute("tabindex", isSelected ? "0" : "-1");
    });
    if (focus) option.focus();
    if (track) {
      dispatchTrack(container, "brand_feedback_rating", {
        source: "Brand FAQ",
        rating: parseInt(value, 10),
      });
    }
  };

  options.forEach((option, index) => {
    option.addEventListener("click", (e) => {
      e.preventDefault();
      select(option);
    });

    option.addEventListener("keydown", (e) => {
      switch (e.key) {
        case "Enter":
        case " ":
          e.preventDefault();
          select(option);
          return;
        case "ArrowRight":
        case "ArrowDown": {
          e.preventDefault();
          const next = options[(index + 1) % options.length];
          select(next, { focus: true, track: false });
          return;
        }
        case "ArrowLeft":
        case "ArrowUp": {
          e.preventDefault();
          const prev = options[(index - 1 + options.length) % options.length];
          select(prev, { focus: true, track: false });
          return;
        }
        case "Home":
          e.preventDefault();
          select(options[0], { focus: true, track: false });
          return;
        case "End":
          e.preventDefault();
          select(options[options.length - 1], { focus: true, track: false });
          return;
        default:
          break;
      }
    });
  });
}

function validate(form) {
  let valid = true;
  let firstInvalidField = null;

  const validateField = (validateAttr, { requireEmail = false } = {}) => {
    const field = form.querySelector(`[data-validate="${validateAttr}"]`);
    if (!field) return;
    clearFieldError(field);
    const { fragnoInput, value } = getFragnoInputValue(field);
    clearFragnoError(fragnoInput);

    let message = null;
    if (!value) {
      message = "This field is required.";
    } else if (requireEmail && !EMAIL_REGEX.test(value)) {
      message = "Please enter a valid email address.";
    }

    if (message) {
      valid = false;
      if (fragnoInput) {
        setFragnoError(fragnoInput, message);
      } else {
        setFieldError(field, message);
      }
      firstInvalidField ||= field;
    }
  };

  validateField("first_name");
  validateField("last_name");
  validateField("email_address", { requireEmail: true });

  const feedbackField = form.querySelector('[data-validate="feedback"]');
  if (feedbackField) {
    clearFieldError(feedbackField);
    const textarea = feedbackField.querySelector("textarea");
    const value = textarea?.value?.trim() || "";
    if (!value) {
      valid = false;
      setFieldError(feedbackField, "This field is required.");
      firstInvalidField ||= feedbackField;
    }
  }

  return { valid, firstInvalidField };
}

function buildFormData(form) {
  const data = new FormData();
  const collect = (validateAttr, name) => {
    const field = form.querySelector(`[data-validate="${validateAttr}"]`);
    const { value } = getFragnoInputValue(field);
    data.set(name, value);
  };

  collect("first_name", "first_name");
  collect("last_name", "last_name");
  collect("email_address", "email_address");

  const feedbackTextarea = form.querySelector(
    '[data-validate="feedback"] textarea',
  );
  data.set("feedback", feedbackTextarea?.value?.trim() || "");

  const hiddenRating = form.querySelector("[data-brand-feedback-rating-input]");
  data.set("rating", hiddenRating?.value || "5");

  return data;
}

async function submitFeedback(container, form) {
  const submitButton = form.querySelector(".brand-feedback__submit");
  submitButton?.setAttribute("loading", "");
  submitButton?.setAttribute("disabled", "");
  const errorMessage = container.dataset.errorMessage || DEFAULT_ERROR_MESSAGE;

  try {
    const response = await fetch(window.location.pathname, {
      method: "POST",
      headers: {
        "X-CSRF-Token": getCsrfToken(),
        Accept: "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: buildFormData(form),
    });

    if (response.ok) {
      const hiddenRating = form.querySelector(
        "[data-brand-feedback-rating-input]",
      );
      dispatchTrack(container, "brand_feedback_submit", {
        source: "Brand FAQ",
        rating: parseInt(hiddenRating?.value || "5", 10),
      });

      const success = container.querySelector("[data-brand-feedback-success]");
      form.hidden = true;
      if (success) {
        success.hidden = false;
        // Focus before scrolling to avoid a race: `preventScroll: true` stops
        // the focus itself from jumping, and the explicit `scrollIntoView`
        // then animates smoothly to the now-focused element.
        success.focus({ preventScroll: true });
        success.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    showToast({ message: errorMessage, tone: "error" });
  } catch (_err) {
    showToast({ message: errorMessage, tone: "error" });
  } finally {
    submitButton?.removeAttribute("loading");
    submitButton?.removeAttribute("disabled");
  }
}

function initBrandFeedbackForm(container) {
  const form = container.querySelector("[data-brand-feedback-body]");
  if (!form) return;

  initRatingPills(container);

  form.addEventListener("submit", (e) => {
    e.preventDefault();
    const { valid, firstInvalidField } = validate(form);
    if (!valid) {
      firstInvalidField?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
      return;
    }
    submitFeedback(container, form);
  });
}

export function initBrandFeedback() {
  document
    .querySelectorAll('[data-component="brand-feedback-form"]')
    .forEach(initBrandFeedbackForm);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initBrandFeedback);
} else {
  initBrandFeedback();
}
